import { withTransaction } from '@elastic/apm-rum-react'
import { Input, InputCountries, InputStates, InputZipCode, LogService, ProfileAdditionalInfo, Tip, User, UserHelper } from '@hub/shared'
import { SqCallout, SqLoader, SqTip } from '@squidit/react-css'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './address.page.scoped.scss'

interface Props {
  addressCompleteness?: boolean
  loadingSubmit?: boolean
  getInfos?: (infos?: ProfileAdditionalInfo) => void
  getValidation?: (valid: boolean) => void
  pushFormSubmit?: () => void
  hasChanged?: (hasChanged?: boolean, message?: string) => any
  impersonateUser?: any
}

type ZipAddress = {
  erro?: boolean
  cep?: string
  localidade?: string
  uf?: string
  bairro?: string
  logradouro?: string
  latitude?: string
  longitude?: string
}

const AddressPage = ({
  addressCompleteness = true,
  loadingSubmit,
  getInfos,
  getValidation,
  pushFormSubmit,
  hasChanged,
  impersonateUser = null,
}: Props) => {
  const { t } = useTranslation('addressPage')
  const userHelper = useMemo(() => new UserHelper(), [])
  const logService = useMemo(() => new LogService(), [])
  const [user, setUser] = useState<User | null>(null)
  const [allValid, setAllValid] = useState(false)
  const [initialAddress, setInitialAddress] = useState<any>({})
  const [address, setAddress] = useState<ProfileAdditionalInfo>(new ProfileAdditionalInfo())
  const [loading, setLoading] = useState<boolean>(true)
  const [searchZip, setSearchZip] = useState<boolean>(false)
  const [autoFilledFields, setAutoFilledFields] = useState<Record<string, boolean>>({})
  const REQUIRED_FIELDS_BR = useMemo(() => ['country', 'city', 'address', 'addressNumber', 'neighborhood', 'uf', 'zipcode'], [])
  const REQUIRED_FIELDS_ALL = useMemo(() => ['country', 'city', 'address', 'uf', 'zipcode'], [])
  const [fieldValidation, setFieldValidation] = useState<Record<string, boolean>>({})
  logService.init('Address Page')

  const submit = (e: any) => {
    e.preventDefault()
    if (allValid && typeof pushFormSubmit === 'function' && (!impersonateUser || impersonateUser?.impersonateAdmin)) {
      pushFormSubmit()
    }
  }

  const checkAllFields = (newAddressValidationObject: any) => {
    const newAllValid = Object.values(newAddressValidationObject).every((value) => value === true)
    setAllValid(newAllValid)
    if (typeof getValidation === 'function') {
      getValidation(newAllValid)
    }
  }

  const isFieldLocked = useCallback(
    (fieldName: string) => {
      return autoFilledFields[fieldName] === true
    },
    [autoFilledFields]
  )

  const onFindAddress = useCallback(
    (zipAddress: ZipAddress) => {
      if (zipAddress && !zipAddress?.erro) {
        if (searchZip) {
          const newAutoFilledFields: Record<string, boolean> = {}

          if (zipAddress?.uf) newAutoFilledFields.uf = true
          if (zipAddress?.localidade) newAutoFilledFields.city = true
          if (zipAddress?.bairro) newAutoFilledFields.neighborhood = true
          if (zipAddress?.logradouro) newAutoFilledFields.address = true

          setAutoFilledFields(newAutoFilledFields)

          const newAddress = new ProfileAdditionalInfo({
            ...address,
            country: 'BR',
            zipcode: zipAddress?.cep?.replace('-', ''),
            city: zipAddress?.localidade,
            uf: zipAddress?.uf,
            neighborhood: zipAddress?.bairro,
            address: zipAddress?.logradouro,
            lat: zipAddress?.latitude,
            lng: zipAddress?.longitude,
          })
          setSearchZip(false)
          setAddress(newAddress)
          if (typeof getInfos === 'function') {
            getInfos(newAddress)
          }
          document.getElementById('number-address')?.focus()
        }
      }
    },
    [searchZip, address, getInfos]
  )

  const validate = useCallback(
    (fieldName: string, fieldValue: boolean) => {
      const newValidation = { ...fieldValidation, [fieldName]: fieldValue }
      setFieldValidation(newValidation)

      const requiredFields = address?.country !== 'BR' ? REQUIRED_FIELDS_ALL : REQUIRED_FIELDS_BR
      const newAllValid = requiredFields.every((field) => newValidation[field] === true)

      setAllValid(newAllValid)
      if (typeof getValidation === 'function') {
        getValidation(newAllValid)
      }
    },
    [fieldValidation, address?.country, REQUIRED_FIELDS_ALL, REQUIRED_FIELDS_BR, getValidation]
  )

  const handleChange = (fieldName: string, fieldValue: any) => {
    let newAddress: any = new ProfileAdditionalInfo({ ...address, [fieldName]: fieldValue })
    if (fieldName === 'country') {
      newAddress = new ProfileAdditionalInfo({
        ...newAddress,
        city: '',
        address: '',
        addressNumber: '',
        neighborhood: '',
        uf: '',
        zipcode: '',
        complement: '',
        lat: null,
        lng: null,
        [fieldName]: fieldValue,
      })
      setAutoFilledFields({})
    }
    if (fieldName === 'zipcode') {
      setSearchZip(true)
      setAutoFilledFields({})
    }
    setAddress(newAddress)
    if (typeof getInfos === 'function') {
      getInfos(newAddress)
    }
    if (typeof hasChanged === 'function') {
      hasChanged(newAddress[fieldName] !== initialAddress[fieldName], t('unsavedDataMessage'))
    }
  }

  useEffect(() => {
    userHelper.getUser().then((user) => {
      setUser(user)
      const instanceOfAddress = new ProfileAdditionalInfo(user?.profileadditionalinfo)
      if (instanceOfAddress?.toValidation) {
        const validationObject = instanceOfAddress?.toValidation(
          instanceOfAddress?.country !== 'BR' ? REQUIRED_FIELDS_ALL : REQUIRED_FIELDS_BR
        )
        setAddress(instanceOfAddress)
        setInitialAddress(instanceOfAddress)
        checkAllFields(validationObject)
        setLoading(false)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userHelper])

  if (loading) {
    return (
      <div className="address-page loader-wrapper">
        <SqLoader size="big" />
      </div>
    )
  }

  return (
    <div className="address-page">
      {!addressCompleteness ? (
        <SqCallout className="mt-0 mb-5" title={t('warning.title')}>
          <span dangerouslySetInnerHTML={{ __html: t('warning.message') }}></span>
        </SqCallout>
      ) : null}
      <form onSubmit={(event) => submit(event)}>
        <InputCountries
          value={address?.country || user?.profileadditionalinfo?.nationality}
          label={t('form.country')}
          id="country-address"
          name="country-address"
          className="mb-3"
          onChange={(value) => handleChange('country', value)}
          onValidate={(valid) => validate('country', valid)}
          disabled={loadingSubmit}
          requiredOnLoad
          required
        />
        <InputZipCode
          value={address?.zipcode}
          label={t('form.zip')}
          id="zipcode-address"
          name="zipcode-address"
          className="mb-3"
          onChange={(value: string) => handleChange('zipcode', value)}
          onValidate={(valid: boolean) => validate('zipcode', valid)}
          country={address?.country || user?.profileadditionalinfo?.nationality}
          readOnly={loadingSubmit}
          onFindAddress={(e: any) => onFindAddress(e)}
          requiredOnLoad
          required
        />
        <InputStates
          value={address?.uf}
          label={t('form.state')}
          id="uf-address"
          name="uf-address"
          className={`mb-3 ${isFieldLocked('uf') ? 'locked-field' : ''}`}
          onChange={(value: string) => handleChange('uf', value)}
          onValidate={(valid: boolean) => validate('uf', valid)}
          country={address?.country}
          disabled={loadingSubmit || isFieldLocked('uf')}
          readOnly={loadingSubmit || isFieldLocked('uf')}
          requiredOnLoad
          required
        />
        <Input
          value={address?.city}
          label={address?.country === 'BR' ? t('form.city') : t('form.cityInter')}
          id="city-address"
          name="city-address"
          className={`mb-3 ${isFieldLocked('city') ? 'locked-field' : ''}`}
          onChange={(value) => handleChange('city', value)}
          onValidate={(valid) => validate('city', valid)}
          readOnly={loadingSubmit || isFieldLocked('city')}
          requiredOnLoad
          required
        />
        {address?.country === 'BR' ? (
          <Input
            value={address?.neighborhood}
            label={t('form.neighborhood')}
            id="neighborhood-address"
            name="neighborhood-address"
            className={`mb-3 ${isFieldLocked('neighborhood') ? 'locked-field' : ''}`}
            onChange={(value) => handleChange('neighborhood', value)}
            onValidate={(valid) => validate('neighborhood', valid)}
            readOnly={loadingSubmit || isFieldLocked('neighborhood')}
            requiredOnLoad
            required
          />
        ) : null}
        <Input
          value={address?.address}
          label={address?.country === 'BR' ? t('form.address') : t('form.addressInter')}
          id="street-address"
          name="street-address"
          className={`mb-3 ${isFieldLocked('address') ? 'locked-field' : ''}`}
          onChange={(value) => handleChange('address', value)}
          onValidate={(valid) => validate('address', valid)}
          readOnly={loadingSubmit || isFieldLocked('address')}
          requiredOnLoad
          required
        />
        {address?.country === 'BR' ? (
          <Input
            value={address?.addressNumber}
            label={
              <div>
                {t('form.number')}
                <SqTip className="ml-1" message={t('form.numberTip')} fontSize="12px" />
              </div>
            }
            id="number-address"
            name="number-address"
            className="mb-3"
            onChange={(value) => handleChange('addressNumber', value)}
            onValidate={(valid) => validate('addressNumber', valid)}
            readOnly={loadingSubmit}
            requiredOnLoad
            required
          />
        ) : null}
        <Input
          value={address?.complement}
          label={t('form.complement')}
          id="complement-address"
          name="complement-address"
          readOnly={loadingSubmit}
          onChange={(value) => handleChange('complement', value)}
          onValidate={(valid) => validate('complement', valid)}
        />
        <button type="submit" style={{ display: 'none' }}></button>
      </form>
    </div>
  )
}

export default withTransaction('AddressPage', 'component')(AddressPage)
